import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ArrowLeft, CheckCircle, XCircle, ExternalLink, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getApiUrl = () => {
  const h = window.location.hostname;
  return (h === 'localhost' || h.includes('preview.emergentagent.com')) 
    ? (BACKEND_URL || '') 
    : window.location.origin;
};
const API = `${getApiUrl()}/api`;

const VerifyContributions = ({ user }) => {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const [group, setGroup] = useState(null);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [memberData, setMemberData] = useState({});
  const [verifying, setVerifying] = useState({});

  useEffect(() => {
    fetchData();
  }, [groupId]);

  const fetchData = async () => {
    try {
      const [groupResponse, pendingResponse] = await Promise.all([
        axios.get(`${API}/groups/${groupId}`),
        axios.get(`${API}/contributions/pending/${groupId}?user_id=${user.id}`)
      ]);
      
      setGroup(groupResponse.data);
      setPending(pendingResponse.data);

      const userIds = [...new Set(pendingResponse.data.map(c => c.user_id))];
      const userData = {};
      for (const userId of userIds) {
        try {
          const userResponse = await axios.get(`${API}/users/${userId}`);
          userData[userId] = userResponse.data;
        } catch (error) {
          userData[userId] = { name: 'Unknown User' };
        }
      }
      setMemberData(userData);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Only group creator can verify contributions');
        navigate(`/group/${groupId}`);
      } else {
        toast.error('Failed to fetch pending contributions');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (contributionId, action) => {
    setVerifying({ ...verifying, [contributionId]: true });
    try {
      await axios.put(
        `${API}/contributions/${contributionId}/verify?user_id=${user.id}&action=${action}`
      );
      toast.success(`Contribution ${action === 'approve' ? 'approved ✅' : 'rejected ❌'}!`);
      
      // Refresh data
      setTimeout(() => {
        fetchData();
      }, 500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to verify contribution');
    } finally {
      setVerifying({ ...verifying, [contributionId]: false });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!group) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button
            variant="ghost"
            onClick={() => navigate(`/group/${groupId}`)}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Group
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <h1 className="text-3xl font-bold text-gray-900 mb-2" data-testid="verify-title">
            Verify Contributions
          </h1>
          <p className="text-gray-600">
            Review and approve payments for {group.name}
          </p>
        </motion.div>

        {pending.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl p-12 text-center shadow-sm border border-gray-200"
          >
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2" data-testid="no-pending-title">
              All Caught Up!
            </h3>
            <p className="text-gray-600">No pending contributions to verify</p>
          </motion.div>
        ) : (
          <div className="space-y-4">
            {pending.map((contribution, index) => {
              const member = memberData[contribution.user_id] || { name: 'Unknown' };
              const isVerifying = verifying[contribution.id];
              return (
                <motion.div
                  key={contribution.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200"
                  data-testid={`pending-contribution-${index}`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                          <span className="text-blue-700 font-semibold text-lg">
                            {member.name[0]}
                          </span>
                        </div>
                        <div>
                          <p className="font-semibold text-lg text-gray-900">{member.name}</p>
                          <p className="text-sm text-gray-600">
                            {new Date(contribution.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <div className="bg-gray-50 rounded-xl p-4 space-y-2 border border-gray-200">
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Amount:</span>
                          <span className="font-bold text-lg text-blue-600">₹{contribution.amount}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">UPI Transaction ID:</span>
                          <span className="font-mono text-sm font-semibold text-gray-900 bg-white px-2 py-1 rounded">
                            {contribution.upi_transaction_id}
                          </span>
                        </div>
                        {contribution.payment_screenshot_url && (
                          <div>
                            <a
                              href={contribution.payment_screenshot_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-blue-600 flex items-center gap-1 hover:text-blue-700"
                            >
                              <ExternalLink className="w-3 h-3" />
                              View Payment Screenshot
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-row lg:flex-col gap-3">
                      <Button
                        onClick={() => handleVerify(contribution.id, 'approve')}
                        disabled={isVerifying}
                        className="flex-1 lg:flex-none rounded-full bg-green-600 hover:bg-green-700 text-white min-w-[140px]"
                        data-testid={`approve-btn-${index}`}
                      >
                        {isVerifying ? (
                          'Verifying...'
                        ) : (
                          <>
                            <CheckCircle className="w-4 h-4 mr-2" /> Approve
                          </>
                        )}
                      </Button>
                      <Button
                        onClick={() => handleVerify(contribution.id, 'reject')}
                        disabled={isVerifying}
                        variant="outline"
                        className="flex-1 lg:flex-none rounded-full border-2 border-red-500 text-red-600 hover:bg-red-50 min-w-[140px]"
                        data-testid={`reject-btn-${index}`}
                      >
                        {isVerifying ? (
                          'Processing...'
                        ) : (
                          <>
                            <XCircle className="w-4 h-4 mr-2" /> Reject
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Info Banner */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-blue-900 font-semibold mb-1">Verification Tips</p>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Verify the UPI transaction ID matches their payment app</li>
                <li>• Check the amount is correct before approving</li>
                <li>• Approved contributions update the group balance immediately</li>
                <li>• Members earn +10 reward points and streak updates on approval</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyContributions;
